import {
  NestedArea,
  NestedHome,
  NestedMunicipality,
  NestedProject,
  TODO,
} from '@habity/types';
import { getMapboxStaticMapUrlFromLngLat } from '../mapbox/static';

/**
 * Get Cloudflare Images URL for a given image ID and variant
 * @param imageId Cloudflare image ID
 * @param variant Variant name (default: 'public')
 * @returns Full URL to the Cloudflare image
 */
export const getCloudflareImageUrl = (
  imageId: string,
  variant = 'public'
): string => {
  const accountHash = process.env.NEXT_PUBLIC_CLOUDFLARE_ACCOUNT_HASH;
  return `https://imagedelivery.net/${accountHash}/${imageId}/${variant}`;
};

/**
 * Get the static map image URL for a home from Cloudflare Images
 * Falls back to Mapbox if Cloudflare image doesn't exist
 * @param home Home object with cloudflare_map_image_id
 * @returns URL to the static map image
 */
export const getCloudflareStaticMapUrlForHome = (
  home: NestedHome
): string | undefined => {
  const { address, cloudflare_map_image_id } = home || {};
  const { longitude, latitude, location } = address;

  if (cloudflare_map_image_id) {
    return getCloudflareImageUrl(cloudflare_map_image_id, 'w=2560,h=1000,fit=contain,quality=95,format=auto');
  }

  const coordinates = longitude && latitude
    ? { longitude, latitude }
    // @ts-ignore
    : location?.coordinates?.length
      // @ts-ignore
      ? { longitude: location.coordinates[0], latitude: location.coordinates[1] }
      : null;

  // Fallback to Mapbox static map if coordinates exist
  if (coordinates) {
    return getMapboxStaticMapUrlFromLngLat(coordinates.longitude, coordinates.latitude, {
      highDefinition: true,
      w: '1280',
      h: '500',
      colored: true,
    });
  }

  return undefined;
};

/**
 * Get the static map image URL for a municipality from Cloudflare Images
 * Falls back to Mapbox if Cloudflare image doesn't exist
 * @param municipality Municipality object with cloudflare_map_image_id
 * @param opts Optional map options - checks dimensions to determine variant (hero vs card)
 * @returns URL to the static map image
 */
export const getCloudflareStaticMapUrlForMunicipality = (
  municipality: NestedMunicipality,
  opts?: TODO
): string | undefined => {
  const {
    cloudflare_map_image_card_id,
    cloudflare_map_image_hero_id,
    location,
  } = municipality;

  // Determine if hero or card image is needed based on dimensions
  // Hero pages use 660x300, cards use 400x520
  const isHero = opts?.w === '660' || opts?.h === '300';

  // Return hero image if requested and available
  if (isHero && cloudflare_map_image_hero_id) {
    return getCloudflareImageUrl(cloudflare_map_image_hero_id, 'public');
  }

  // Return card image if available
  if (cloudflare_map_image_card_id) {
    return getCloudflareImageUrl(cloudflare_map_image_card_id, 'public');
  }

  if (location) {
    // For now, return undefined to force fallback to placeholder
    // The old Mapbox implementation can be imported if needed
    return undefined;
  }

  return undefined;
};

/**
 * Get the static map image URL for an area from Cloudflare Images
 * Falls back to Mapbox if Cloudflare image doesn't exist
 * @param area Area object with cloudflare_map_image_id
 * @param opts Optional map options - checks dimensions to determine variant (hero vs card)
 * @returns URL to the static map image
 */
export const getCloudflareStaticMapUrlForArea = (
  area: NestedArea,
  opts?: TODO
): string | undefined => {
  const {
    cloudflare_map_image_card_id,
    cloudflare_map_image_hero_id,
    location,
  } = area;

  // Determine if hero or card image is needed based on dimensions
  // Hero pages use 660x300, cards use 400x520
  const isHero = opts?.w === '660' || opts?.h === '300';

  // Return hero image if requested and available
  if (isHero && cloudflare_map_image_hero_id) {
    return getCloudflareImageUrl(cloudflare_map_image_hero_id, 'public');
  }

  // Return card image if available
  if (cloudflare_map_image_card_id) {
    return getCloudflareImageUrl(cloudflare_map_image_card_id, 'public');
  }

  if (location) {
    // For now, return undefined to force fallback to placeholder
    // The old Mapbox implementation can be imported if needed
    return undefined;
  }

  return undefined;
};

/**
 * Get the static map image URL for a project from Cloudflare Images
 * Falls back to Mapbox if Cloudflare image doesn't exist
 * @param project Project object with cloudflare_map_image_id
 * @param opts Optional map options (ignored when using Cloudflare, kept for backward compatibility)
 * @returns URL to the static map image
 */
export const getCloudflareStaticMapUrlForProject = (
  project: NestedProject,
  opts?: TODO
): string | undefined => {
  const { cloudflare_map_image_id, longitude, latitude } = project;

  // Use Cloudflare image if available
  if (cloudflare_map_image_id) {
    return getCloudflareImageUrl(cloudflare_map_image_id, 'w=2560,h=1000,fit=contain,quality=95,format=auto');
  }

  // Fallback to Mapbox static map if coordinates exist
  if (longitude && latitude) {
    return getMapboxStaticMapUrlFromLngLat(longitude, latitude, {
      highDefinition: true,
      w: '1280',
      h: '500',
      colored: true,
    });
  }

  return undefined;
};
